import { logger } from '@adornis/base/logging.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import * as crypto from 'node:crypto';
import { GRAMMAR_TASK_TYPES } from '../../shared/grammar-grading.js';
import { loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { joinTaskRefSegments } from '../utils/task-ref.js';
import { resolveTaskTreePath, taskTreeIndexPathString } from '../utils/task-tree-path.js';
import { TaskType } from './task-types/task-main.js';

interface NestedTask {
  _id?: string;
  taskNumber?: string;
  taskType?: TaskType;
  subTasks?: NestedTask[];
  masterSolution?: any;
  gradingScheme?: any;
}

function resolveTaskNumber(tasks: NestedTask[] | undefined, taskReference: string): string | null {
  const resolved = resolveTaskTreePath(tasks, taskReference, 'subTasks');
  if (resolved.status !== 'found') return null;
  return joinTaskRefSegments(resolved.taskNumberPath) || null;
}

// Resolve a task by _id/taskNumber to both the matched task object and its MongoDB dot-notation
// path (so callers can run atomic array updates). Recurses into subTasks like the other helpers.
function findTaskWithPath(tasks: NestedTask[] | undefined, ref: string): { path: string; task: NestedTask } | null {
  const resolved = resolveTaskTreePath(tasks, ref, 'subTasks');
  if (resolved.status !== 'found') return null;
  return { path: taskTreeIndexPathString('tasks', 'subTasks', resolved.indexPath), task: resolved.node };
}

function resolveTaskPath(tasks: NestedTask[] | undefined, taskReference: string): string[] | null {
  const resolved = resolveTaskTreePath(tasks, taskReference, 'subTasks');
  return resolved.status === 'found' ? resolved.taskNumberPath : null;
}

// Orders "|"-joined qualified paths the way a teacher expects to read them (1, 1|a, 1|b, 2, 10 — not
// the lexicographic 1, 1|a, 10, 2), comparing segment by segment and numerically wherever a segment
// parses as a number. A path that is a prefix of another (e.g. "1" vs "1|a") sorts first.
function compareQualifiedPaths(a: string, b: string): number {
  const segmentsA = a.split('|');
  const segmentsB = b.split('|');
  const length = Math.max(segmentsA.length, segmentsB.length);
  for (let i = 0; i < length; i++) {
    const segmentA = segmentsA[i];
    const segmentB = segmentsB[i];
    if (segmentA === undefined) return -1;
    if (segmentB === undefined) return 1;
    if (segmentA === segmentB) continue;
    const numA = Number(segmentA);
    const numB = Number(segmentB);
    if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
    return segmentA.localeCompare(segmentB);
  }
  return 0;
}

// Reverse of resolveTaskPath: given the taskNumber segments of a qualified path (e.g. from a
// LanguageUseScheme.dependsOn entry split on "|"), find the matching task node by walking down by
// OWN taskNumber at each depth — never by _id, since a path segment is always a local taskNumber.
function findTaskByPathSegments(tasks: NestedTask[] | undefined, segments: string[]): NestedTask | null {
  if (!Array.isArray(tasks) || segments.length === 0) return null;
  const [head, ...rest] = segments;
  for (const task of tasks) {
    const taskNumber = task?.taskNumber !== undefined ? String(task.taskNumber) : '';
    if (taskNumber !== head) continue;
    if (rest.length === 0) return task;
    const found = findTaskByPathSegments(task.subTasks, rest);
    if (found) return found;
  }
  return null;
}

function updateTaskRecursively(
  tasks: NestedTask[] | undefined,
  taskReference: string,
  updater: (task: NestedTask) => void,
): boolean {
  const resolved = resolveTaskTreePath(tasks, taskReference, 'subTasks');
  if (resolved.status !== 'found') return false;
  updater(resolved.node);
  return true;
}

export const updateExamTaskMasterSolutionMutation = registerMutation({
  operationName: 'updateExamTaskMasterSolutionMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'taskReference', type: () => String },
    { name: 'masterSolutionContent', type: () => String },
  ],
  resolve: (examId: string, taskReference: string, masterSolutionContent: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const updated = updateTaskRecursively(exam.tasks as NestedTask[] | undefined, taskReference, task => {
      const existingMasterSolution = task.masterSolution?.doc || task.masterSolution || {};
      task.masterSolution = {
        ...existingMasterSolution,
        _class: 'MasterSolution',
        examId,
        taskNumber: task.taskNumber ?? taskReference,
        content: masterSolutionContent,
        // Provenance is only stamped when the section had none: this task's solution is being written by the
        // teacher here, and without a value the display would infer text-without-boxes as AI-generated. An
        // EDIT keeps whatever was there — where content originally came from does not change because it was
        // adjusted, and `isApproved` already records the teacher's sign-off.
        source: existingMasterSolution.source ?? 'manual',
        feedback: Array.isArray(existingMasterSolution.feedback) ? existingMasterSolution.feedback : [],
      };
    });

    if (!updated) {
      throw new Error(`Task ${taskReference} wurde in Klausur ${examId} nicht gefunden.`);
    }

    await examCollection.updateOne(
      { _id: examId },
      {
        $set: {
          tasks: exam.tasks,
        },
      },
    );

    const resolvedTaskRef = resolveTaskNumber(exam.tasks as NestedTask[] | undefined, taskReference);
    const updatedExamForRegrade = await examCollection.findOne({ _id: examId });
    if (resolvedTaskRef && updatedExamForRegrade?.examState?.hasGradingStarted) {
      const { regradeTaskForAllSubmissions } = await import('./agents/gradingAgent.js');
      void regradeTaskForAllSubmissions(examId, resolvedTaskRef, 'content').catch(error => {
        logger.error({ err: error, resolvedTaskRef, context: 'updateExamTask' }, 'MS regrading failed for task');
      });
    }

    return true;
  },
});

export const updateExamTaskGradingSchemeMutation = registerMutation({
  operationName: 'updateExamTaskGradingSchemeMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'taskReference', type: () => String },
    { name: 'criteria', type: () => String },
    { name: 'maxPoints', type: () => Float },
  ],
  resolve: (examId: string, taskReference: string, criteria: string, maxPoints: number) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const updated = updateTaskRecursively(exam.tasks as NestedTask[] | undefined, taskReference, task => {
      const existingGradingScheme = task.gradingScheme?.doc || task.gradingScheme || {};
      task.gradingScheme = {
        ...existingGradingScheme,
        _class: 'GradingScheme',
        examId,
        criteria,
        maxPoints,
        source: existingGradingScheme.source ?? 'manual',
        feedback: Array.isArray(existingGradingScheme.feedback) ? existingGradingScheme.feedback : [],
      };
    });

    if (!updated) {
      throw new Error(`Task ${taskReference} wurde in Klausur ${examId} nicht gefunden.`);
    }

    await examCollection.updateOne(
      { _id: examId },
      {
        $set: {
          tasks: exam.tasks,
        },
      },
    );

    const resolvedTaskRef = resolveTaskNumber(exam.tasks as NestedTask[] | undefined, taskReference);
    const updatedExamForRegrade = await examCollection.findOne({ _id: examId });
    const willRegrade = !!resolvedTaskRef && !!updatedExamForRegrade?.examState?.hasGradingStarted;
    if (willRegrade) {
      const { regradeTaskForAllSubmissions } = await import('./agents/gradingAgent.js');
      void regradeTaskForAllSubmissions(examId, resolvedTaskRef!, 'content').catch(error => {
        logger.error({ err: error, resolvedTaskRef, context: 'updateExamTask' }, 'GS regrading failed for task');
      });
    }

    return true;
  },
});

export const updateLanguageUseSchemeMutation = registerMutation({
  operationName: 'updateLanguageUseSchemeMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'languageUseSchemeId', type: () => String },
    { name: 'criteria', type: () => String },
    { name: 'maxPoints', type: () => Float },
    // Optional — lets the grading canvas's "approve" button set LanguageUseScheme.isApproved without a
    // dedicated mutation, mirroring approveMsSectionMutation's own-criteria approve pattern. Only
    // written when explicitly passed (undefined leaves the existing flag untouched).
    { name: 'isApproved', type: () => Boolean },
  ],
  resolve:
    (examId: string, languageUseSchemeId: string, criteria: string, maxPoints: number, isApproved?: boolean) =>
    async () => {
      await loadAndAssert(Exam, examId, 'update', { _id: 1 });
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });

      if (!exam) {
        throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
      }

      const languageUseSchemeRules: any[] = Array.isArray((exam as any).languageUseSchemeRules)
        ? (exam as any).languageUseSchemeRules
        : [];
      const idx = languageUseSchemeRules.findIndex(scheme => String(scheme?._id) === String(languageUseSchemeId));
      if (idx === -1) {
        throw new Error(`LanguageUseScheme ${languageUseSchemeId} wurde in Klausur ${examId} nicht gefunden.`);
      }

      // A pure approve click (Review button) re-sends the SAME criteria/maxPoints it just read, only
      // flipping isApproved — that must NOT re-trigger a regrade, exactly like approveMsSectionMutation
      // never regrades for own-criteria approve. Without this guard, editing+saving a group and then
      // approving it fires TWO overlapping runGradingAgent calls for the same tasks, which race on the
      // shared examState.gradingProgress total/finished counters (each reads a stale total, decides its
      // own reset-vs-decrement independently, and both apply — this is what produced negative `finished`).
      const existingScheme = languageUseSchemeRules[idx];
      const contentChanged =
        String(existingScheme?.criteria ?? '') !== String(criteria ?? '') ||
        Number(existingScheme?.maxPoints ?? 0) !== Number(maxPoints ?? 0);

      const updatedRules = languageUseSchemeRules.map((scheme, i) =>
        i === idx
          ? {
              ...scheme,
              criteria,
              maxPoints,
              // Consumed the moment real points are set — see LanguageUseScheme.carriedOverFromGroup's doc
              // (exam.ts) and updateLanguageUseSchemeMembershipMutation, which sets it on a rule split off a
              // group and deliberately skips grading it at 0 points. `contentChanged` below (computed
              // from the OLD maxPoints, which was 0) is what actually triggers the regrade once cleared.
              carriedOverFromGroup: maxPoints > 0 ? null : (scheme?.carriedOverFromGroup ?? null),
              ...(typeof isApproved === 'boolean' ? { isApproved } : {}),
            }
          : scheme,
      );
      const dependsOn: string[] = Array.isArray(updatedRules[idx]?.dependsOn) ? updatedRules[idx].dependsOn : [];

      await examCollection.updateOne({ _id: examId }, { $set: { languageUseSchemeRules: updatedRules } });

      const updatedExamForRegrade = await examCollection.findOne({ _id: examId });
      const willRegrade =
        contentChanged && dependsOn.length > 0 && !!updatedExamForRegrade?.examState?.hasGradingStarted;
      if (willRegrade) {
        const { regradeTaskNumbersForAllSubmissions } = await import('./agents/gradingAgent.js');
        // Scoped to THIS scheme only — dependsOn's tasks can also belong to other groups (a task can be
        // in more than one), and those must not be swept into a regrade just because they share a
        // member task with the one that actually changed.
        void regradeTaskNumbersForAllSubmissions(examId, dependsOn, 'languageUse', languageUseSchemeId).catch(error => {
          logger.error(
            { err: error, languageUseSchemeId, context: 'updateExamTask' },
            'LanguageUseScheme regrading failed',
          );
        });
      }

      return true;
    },
});

export const updateLanguageUseSchemeMembershipMutation = registerMutation({
  operationName: 'updateLanguageUseSchemeMembershipMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'languageUseSchemeId', type: () => String },
    { name: 'nextTaskIds', type: () => [String] },
  ],
  resolve: (examId: string, languageUseSchemeId: string, nextTaskIds: string[]) => async () => {
    if (!Array.isArray(nextTaskIds) || nextTaskIds.length === 0) {
      throw new ExpectedError('Eine Grammatik-Regel benötigt mindestens eine zugeordnete Aufgabe.');
    }

    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    if (!exam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const tasks = exam.tasks as NestedTask[] | undefined;
    const wasAlreadyGraded = !!(exam as any).examState?.hasGradingStarted;
    const languageUseSchemeRules: any[] = Array.isArray((exam as any).languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [];
    const targetIdx = languageUseSchemeRules.findIndex(scheme => String(scheme?._id) === String(languageUseSchemeId));
    if (targetIdx === -1) {
      throw new Error(`LanguageUseScheme ${languageUseSchemeId} wurde in Klausur ${examId} nicht gefunden.`);
    }
    const targetScheme = languageUseSchemeRules[targetIdx];
    const oldDependsOn: string[] = Array.isArray(targetScheme?.dependsOn) ? targetScheme.dependsOn : [];

    const resolvedNext = nextTaskIds
      .map(taskId => {
        const segments = resolveTaskPath(tasks, taskId);
        const node = segments ? findTaskByPathSegments(tasks, segments) : null;
        return segments && node ? { qualifiedPath: segments.join('|'), node } : null;
      })
      .filter((entry): entry is { qualifiedPath: string; node: NestedTask } => entry !== null);

    const nextDependsOn = Array.from(new Set(resolvedNext.map(entry => entry.qualifiedPath))).sort(
      compareQualifiedPaths,
    );
    if (nextDependsOn.length === 0) {
      throw new ExpectedError('Keine der ausgewählten Aufgaben konnte in der Klausur gefunden werden.');
    }

    const oldSet = new Set(oldDependsOn);
    const nextSet = new Set(nextDependsOn);
    const addedPaths = nextDependsOn.filter(path => !oldSet.has(path));
    const removedPaths = oldDependsOn.filter(path => !nextSet.has(path));

    // Added tasks: the target group now governs them, so delete any solo (1-member) LanguageUseScheme
    // they were carrying — it's being absorbed into the target group. No separate regrade needed for
    // these: the group-level regrade below (groupRefs, which includes nextDependsOn) already covers
    // every current member of the target group, added ones included.
    const soloSchemeIdsToDelete = new Set<string>();
    for (const path of addedPaths) {
      const soloScheme = languageUseSchemeRules.find(
        scheme =>
          String(scheme?._id) !== String(languageUseSchemeId) &&
          Array.isArray(scheme?.dependsOn) &&
          scheme.dependsOn.length === 1 &&
          scheme.dependsOn[0] === path,
      );
      if (soloScheme?._id) soloSchemeIdsToDelete.add(String(soloScheme._id));
    }

    // Removed tasks: a grammar-eligible task needs its own non-empty rule so it doesn't silently fall
    // back to "no rule configured" — a brand-new solo LanguageUseScheme, pre-filled with the group's
    // current criteria, 0 points so it surfaces via the existing zero-points/uncertainty-queue
    // machinery (hasZeroGroupLanguageUsePoints). Non-eligible tasks are just dropped, no rule created.
    //
    // Deliberately not regraded immediately: this placeholder is worth 0 points, so there is nothing
    // gradeable yet — grading it now would waste an LLM call against a rubric worth nothing.
    // carriedOverFromGroup marks it as a deferred case, resolved by updateLanguageUseSchemeMutation the
    // moment a teacher gives it real points — that mutation's own regrade-on-save is what actually
    // grades it then. Only set when the exam had already started grading: if the group was never
    // graded, this task never had a real grade to carry over, so it's just a normal never-graded rule.
    const newSoloSchemes: any[] = [];
    for (const path of removedPaths) {
      const node = findTaskByPathSegments(tasks, path.split('|'));
      if (!node || !node.taskType || !GRAMMAR_TASK_TYPES.has(String(node.taskType))) continue;
      newSoloSchemes.push({
        _id: crypto.randomUUID(),
        _class: 'LanguageUseScheme',
        criteria: String(targetScheme?.criteria ?? ''),
        maxPoints: 0,
        confidence: null,
        isApproved: false,
        carriedOverFromGroup: wasAlreadyGraded ? true : null,
        dependsOn: [path],
      });
    }
    if (newSoloSchemes.length > 0) {
      const { allocateLanguageUseSchemeDisplayNumbers } = await import('./utils/allocate-language-use-scheme-numbers.js');
      const numbers = await allocateLanguageUseSchemeDisplayNumbers(examId, newSoloSchemes.length);
      newSoloSchemes.forEach((scheme, index) => {
        scheme.displayNumber = numbers[index];
      });
    }

    // Any OTHER scheme that had a now-added task as one of its own members loses it here; dropped
    // entirely if that empties it (a 1-member remainder is intentionally kept — see plan).
    const otherSchemeRemainingPaths: string[] = [];
    const updatedRules = languageUseSchemeRules
      .filter(scheme => !soloSchemeIdsToDelete.has(String(scheme?._id ?? '')))
      .map(scheme => {
        if (String(scheme?._id) === String(languageUseSchemeId)) return { ...scheme, dependsOn: nextDependsOn };
        const otherDependsOn: string[] = Array.isArray(scheme?.dependsOn) ? scheme.dependsOn : [];
        const filtered = otherDependsOn.filter(path => !addedPaths.includes(path));
        if (filtered.length === otherDependsOn.length) return scheme;
        if (filtered.length > 0) otherSchemeRemainingPaths.push(...filtered);
        return { ...scheme, dependsOn: filtered };
      })
      .filter(scheme => (Array.isArray(scheme?.dependsOn) ? scheme.dependsOn.length > 0 : true))
      .concat(newSoloSchemes);

    await examCollection.updateOne({ _id: examId }, { $set: { languageUseSchemeRules: updatedRules } });

    const updatedExamForRegrade = await examCollection.findOne({ _id: examId });
    if (updatedExamForRegrade?.examState?.hasGradingStarted) {
      const { regradeTaskNumbersForAllSubmissions } = await import('./agents/gradingAgent.js');
      const groupRefs = Array.from(new Set([...oldDependsOn, ...nextDependsOn]));
      if (groupRefs.length > 0) {
        // Scoped to THIS scheme — its members can also belong to other groups, which must not be
        // regraded just because they share a member task with the group whose membership changed.
        void regradeTaskNumbersForAllSubmissions(examId, groupRefs, 'languageUse', languageUseSchemeId).catch(error => {
          logger.error(
            { err: error, languageUseSchemeId, context: 'updateLanguageUseSchemeMembership' },
            'Group regrading failed',
          );
        });
      }
      if (otherSchemeRemainingPaths.length > 0) {
        // NOT scoped to one scheme id — these paths can belong to several DIFFERENT other schemes (each
        // one that lost a member above), so this intentionally grades whichever scheme(s) now own them.
        void regradeTaskNumbersForAllSubmissions(examId, otherSchemeRemainingPaths, 'languageUse').catch(error => {
          logger.error({ err: error, context: 'updateLanguageUseSchemeMembership' }, 'Other-scheme regrading failed');
        });
      }
    }

    return true;
  },
});

export const deleteLanguageUseSchemeMutation = registerMutation({
  operationName: 'deleteLanguageUseSchemeMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'languageUseSchemeId', type: () => String },
  ],
  resolve: (examId: string, languageUseSchemeId: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    if (!exam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }
    if (Number((exam as any).examState?.isCurrentlyGrading ?? 0) > 0) {
      throw new ExpectedError('Klausur wird gerade benotet — bitte warten, bevor eine Regel gelöscht wird.');
    }

    const languageUseSchemeRules: any[] = Array.isArray((exam as any).languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [];
    if (!languageUseSchemeRules.some(scheme => String(scheme?._id) === String(languageUseSchemeId))) {
      throw new Error(`LanguageUseScheme ${languageUseSchemeId} wurde in Klausur ${examId} nicht gefunden.`);
    }

    const updatedRules = languageUseSchemeRules.filter(scheme => String(scheme?._id) !== String(languageUseSchemeId));
    await examCollection.updateOne({ _id: examId }, { $set: { languageUseSchemeRules: updatedRules } });

    const { deleteLanguageUseRuleResultsForScheme } = await import('./language-use-rule-result.js');
    await deleteLanguageUseRuleResultsForScheme(examId, languageUseSchemeId);

    return true;
  },
});

export const approveMsSectionMutation = registerMutation({
  operationName: 'approveMsSectionMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'taskReference', type: () => String },
    { name: 'solutionType', type: () => String },
  ],
  resolve: (examId: string, taskReference: string, solutionType: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const taskPath = resolveTaskPath(exam.tasks as NestedTask[] | undefined, taskReference);

    updateTaskRecursively(exam.tasks as NestedTask[] | undefined, taskReference, task => {
      const raw = solutionType === 'ms' ? task.masterSolution : task.gradingScheme;
      const source = raw?.doc ?? raw;
      if (!source) return;
      source.isApproved = true;
    });

    await examCollection.updateOne({ _id: examId }, { $set: { tasks: exam.tasks } });

    if (taskPath) {
      const progressKey = solutionType === 'ms' ? 'masterSolutionProgress' : 'gradingSchemeProgress';
      await examCollection.updateOne(
        { _id: examId },
        {
          $pull: {
            [`examState.${progressKey}.lowConfRegions`]: { taskPath },
            [`examState.${progressKey}.errorRegions`]: { taskPath },
          } as any,
        },
      );
    }

    return true;
  },
});

export const updateExamTaskTypeMutation = registerMutation({
  operationName: 'updateExamTaskTypeMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'taskReference', type: () => String },
    { name: 'taskType', type: () => String },
  ],
  resolve: (examId: string, taskReference: string, taskType: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    const updated = updateTaskRecursively(exam.tasks as NestedTask[] | undefined, taskReference, task => {
      task.taskType = taskType as TaskType;
    });

    if (!updated) {
      throw new Error(`Task ${taskReference} wurde in Klausur ${examId} nicht gefunden.`);
    }

    await examCollection.updateOne(
      { _id: examId },
      {
        $set: {
          tasks: exam.tasks,
        },
      },
    );

    return true;
  },
});

export const deleteBoundingBoxbyImageIdMutation = registerMutation({
  operationName: 'deleteBoundingBoxbyImageIdMutation', // Updated operation name
  type: () => Boolean,
  params: [
    { name: 'imageId', type: () => String }, // Changed to imageId
    { name: 'examId', type: () => String },
    { name: 'taskReference', type: () => String },
    { name: 'type', type: () => String },
  ],
  resolve:
    (
      imageId: string, // Changed to imageId
      examId: string,
      taskReference: string,
      type: 'description' | 'masterSolution' | 'gradingScheme',
    ) =>
    async () => {
      await loadAndAssert(Exam, examId, 'update', { _id: 1 });
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });

      if (!exam) {
        throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
      }

      // Find the MongoDB dot-notation path to the task. Tasks are matched by _id or taskNumber
      // (the same identifiers the rest of this file uses) and may be nested under subTasks.
      const resolvedTask = resolveTaskTreePath(exam.tasks as NestedTask[] | undefined, taskReference, 'subTasks');

      if (resolvedTask.status !== 'found') {
        throw new Error(`Task ${taskReference} wurde in Klausur ${examId} nicht gefunden.`);
      }

      const taskPath = taskTreeIndexPathString('tasks', 'subTasks', resolvedTask.indexPath);

      // Bounding boxes are stored flat: description boxes directly on the task, MS/GS boxes under
      // the respective solution sub-document (entities serialize to plain objects, never `.doc`).
      let targetArrayField = `${taskPath}.boundingBoxes`;
      if (type === 'masterSolution') {
        targetArrayField = `${taskPath}.masterSolution.boundingBoxes`;
      } else if (type === 'gradingScheme') {
        targetArrayField = `${taskPath}.gradingScheme.boundingBoxes`;
      }

      // Atomically remove only the bounding box(es) whose imageId matches.
      await examCollection.updateOne({ _id: examId }, {
        $pull: {
          [targetArrayField]: { imageId },
        },
      } as any);

      return true;
    },
});

export const addBoundingBoxByImageIdMutation = registerMutation({
  operationName: 'addBoundingBoxByImageIdMutation',
  type: () => Boolean,
  params: [
    { name: 'imageId', type: () => String },
    { name: 'examId', type: () => String },
    { name: 'taskReference', type: () => String },
    { name: 'type', type: () => String },
  ],
  resolve:
    (
      imageId: string,
      examId: string,
      taskReference: string,
      type: 'description' | 'masterSolution' | 'gradingScheme',
    ) =>
    async () => {
      if (!imageId) {
        throw new Error('imageId ist erforderlich.');
      }
      // The id is copied into the exam, whose files are later shared on result release — only the caller's own.
      await loadAndAssert(AdornisFile, imageId, 'read', { _id: 1 });

      await loadAndAssert(Exam, examId, 'update', { _id: 1 });
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });

      if (!exam) {
        throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
      }

      const found = findTaskWithPath(exam.tasks as NestedTask[] | undefined, taskReference);

      if (!found) {
        throw new Error(`Task ${taskReference} wurde in Klausur ${examId} nicht gefunden.`);
      }

      const { path: taskPath, task } = found;

      // A manually uploaded image is a pure image, classified 'image' just like an OCR-detected image
      // region. Existing text content stays visible because the section's display type also factors in
      // the solution's text content (see deriveSectionContentType in get-master-solutions). Pre-approved
      // so it never shows up as a low-confidence region to review.
      const newBox = {
        _id: crypto.randomUUID(),
        _class: 'BoundingBox',
        imageId,
        contentType: 'image' as const,
        approved: true,
        confidence: 1,
        isHandwritten: false,
        ocrExtractedText: '',
        pageNumber: null,
        topLeft: { _class: 'Point', x: -1, y: -1 },
        bottomRight: { _class: 'Point', x: -1, y: -1 },
      };

      if (type === 'description') {
        await examCollection.updateOne({ _id: examId }, { $push: { [`${taskPath}.boundingBoxes`]: newBox } } as any);
        return true;
      }

      // MS/GS boxes live on the solution sub-document. If it already exists, append atomically;
      // otherwise create a well-formed solution doc (matching updateExamTask*Mutation) holding the box.
      const existingSolution =
        type === 'masterSolution'
          ? (task.masterSolution?.doc ?? task.masterSolution)
          : (task.gradingScheme?.doc ?? task.gradingScheme);

      if (existingSolution && typeof existingSolution === 'object') {
        await examCollection.updateOne({ _id: examId }, {
          $push: { [`${taskPath}.${type}.boundingBoxes`]: newBox },
        } as any);
        return true;
      }

      const solutionDoc =
        type === 'masterSolution'
          ? {
              _class: 'MasterSolution',
              examId,
              taskNumber: task.taskNumber ?? taskReference,
              content: '',
              feedback: [],
              boundingBoxes: [newBox],
            }
          : {
              _class: 'GradingScheme',
              examId,
              taskNumber: task.taskNumber ?? taskReference,
              criteria: '',
              maxPoints: 0,
              feedback: [],
              boundingBoxes: [newBox],
            };

      await examCollection.updateOne({ _id: examId }, { $set: { [`${taskPath}.${type}`]: solutionDoc } } as any);

      return true;
    },
});
